import * as yup from 'yup';
import * as budgetApi from '../../lib/api/budget';
import { useCallback, useEffect, useState } from 'react';
import { useSnackbar } from 'notistack';
import { useFormik } from 'formik';
// @mui
import Button from '@mui/material/Button';
import DialogContent from '@mui/material/DialogContent';
import Stack from '@mui/material/Stack';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import FormControl from '@mui/material/FormControl';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import DialogActions from '@mui/material/DialogActions';
import BootstrapDialog, {
  BootstrapDialogTitle,
} from '../../components/common/dialog/BootstrapDialog';
import { useBoolean } from '../../hooks/useBoolean';
import { getYearOptions, thisYear } from '../../utils/calcDatetime';
import FormLabel from '../../components/common/form/FormLabel';
import NumberFormatText from '../../components/common/form/NumberFormatText';

//----------------------------------------------
const arrYears = getYearOptions(3, 3);
const defaultYear = thisYear;
//----------------------------------------------

const FourMajorInsuranceModalContainer = ({
  title,
  open,
  onClose,
  refreshOnClose,
}) => {
  const isLoading = useBoolean();
  const { enqueueSnackbar } = useSnackbar();
  const [selectedYear, setSelectedYear] = useState(defaultYear);

  const [initialValues, setInitialValues] = useState({
    nationalPension: '',
    healthInsurance: '',
    employeInsurance: '',
    accidentInsurance: '',
  });

  const onLoadData = useCallback(async () => {
    isLoading.onTrue();

    try {
      const res = await budgetApi.getAllMajorInsurance({
        year: selectedYear,
      });

      if (res.data?.info) {
        setInitialValues({ ...res.data.info });
      }
    } catch (err) {
      let message =
        typeof err.response !== 'undefined'
          ? err.response.data.message
          : err.message;
      enqueueSnackbar(message, {
        variant: 'error',
      });
    }
    isLoading.onFalse();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedYear]);

  // 년도와 사원이 선택되면 API 호출함. (변경되어도 재호출)
  useEffect(() => {
    if (selectedYear) {
      onLoadData();
    }
    return () => isLoading.onFalse();
  }, [onLoadData, selectedYear]);

  const validationSchema = yup.object({
    nationalPension: yup
      .number('숫자만 가능합니다.')
      .required('국민연금의 보험요율을 입력하세요.')
      .positive('양수만 가능합니다'),

    healthInsurance: yup
      .number('숫자만 가능합니다.')
      .required('건강보험의 보험요율을 입력하세요.')
      .positive('양수만 가능합니다'),
    employeInsurance: yup
      .number('숫자만 가능합니다.')
      .required('고용보험의 보험요율을 입력하세요.')
      .positive('양수만 가능합니다'),
    accidentInsurance: yup
      .number('숫자만 가능합니다.')
      .required('산재보험의 보험요율을 입력하세요.')
      .positive('양수만 가능합니다'),
  });

  const formik = useFormik({
    initialValues: initialValues,
    enableReinitialize: true,
    validationSchema: validationSchema,
    onSubmit: (values, { setSubmitting }) => {
      setSubmitting(true);
      onSave(values);
      formik.resetForm();
      setSubmitting(false);
    },
  });

  const handleCreate = async () => {
    isLoading.onTrue();

    const newData = {
      year: selectedYear,
      nationalPension: Number(formik.values.nationalPension),
      healthInsurance: Number(formik.values.healthInsurance),
      employeInsurance: Number(formik.values.employeInsurance),
      accidentInsurance: Number(formik.values.accidentInsurance),
    };

    try {
      const response = await budgetApi.saveAllMajorInsurance(newData);
      
      if (response.data.result === 'success') {
        refreshOnClose();
        formik.resetForm();
        enqueueSnackbar(`${selectedYear}년 4대보험 보험요율이 저장되었습니다`, {
          variant: 'success',
        });
      }
    } catch (err) {
      let message =
        typeof err.response !== 'undefined'
          ? err.response.data.message
          : err.message;
      enqueueSnackbar(message, {
        variant: 'error',
      });
    } finally {
      isLoading.onFalse();
    }
  };

  const onSave = async (values) => {
    isLoading.onTrue();

    const newData = {
      year: selectedYear,
      nationalPension: Number(values.nationalPension),
      healthInsurance: Number(values.healthInsurance),
      employeInsurance: Number(values.employeInsurance),
      accidentInsurance: Number(values.accidentInsurance),
    };

    try {
      const response = await budgetApi.updateAllMajorInsurance(newData);
      if (response.data.result === 'success') {
        refreshOnClose();
        enqueueSnackbar(
          `${selectedYear}년 4대보험 보험요율이 일괄 적용 되었습니다`,
          {
            variant: 'success',
          },
        );
      }
    } catch (err) {
      let message =
        typeof err.response !== 'undefined'
          ? err.response.data.message
          : err.message;
      enqueueSnackbar(message, {
        variant: 'error',
      });
    } finally {
      isLoading.onFalse();
    }
  };

  const handleClose = () => {
    formik.resetForm();
    onClose();
  };

  return (
    <BootstrapDialog
      open={open}
      onClose={handleClose}
      fullWidth={true}
      maxWidth="sm"
    >
      <Box
        component="form"
        sx={{
          display: 'flex',
          flexDirection: 'column',
          marginTop: 2,
          px: 2,
        }}
        noValidate
        autoComplete="off"
        onSubmit={formik.handleSubmit}
      >
        <BootstrapDialogTitle onClose={handleClose}>
          {title}
        </BootstrapDialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              sx={{
                py: 1,
                borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
              }}
            >
              <Box sx={{ width: '180px' }} textAlign="center">
                <FormLabel sx={{ px: 1 }}>적용년도</FormLabel>
              </Box>
              <Box>
                <FormControl variant="standard" size="small" fullWidth>
                  <Select
                    variant="outlined"
                    size="small"
                    labelId="selected-year-label"
                    id="selected-year"
                    name="selected-year"
                    displayEmpty
                    sx={{ width: 100 }}
                    value={selectedYear}
                    onChange={(e) => setSelectedYear(e.target.value)}
                  >
                    {arrYears &&
                      arrYears.map((item, index) => (
                        <MenuItem key={index} value={item}>
                          {item}
                        </MenuItem>
                      ))}
                  </Select>
                </FormControl>
              </Box>
            </Stack>
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              sx={{
                py: 1,
                borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
              }}
            >
              <Box sx={{ width: '180px' }} textAlign="center">
                <FormLabel sx={{ px: 1 }}>국민연금</FormLabel>
              </Box>
              <Box>
                <TextField
                  size="small"
                  required
                  variant="outlined"
                  fullWidth
                  id="nationalPension"
                  name="nationalPension"
                  value={formik.values.nationalPension}
                  onChange={(e) =>
                    formik.setFieldValue(
                      'nationalPension',
                      Number(e.target.value),
                    )
                  }
                  error={
                    formik.touched.nationalPension &&
                    Boolean(formik.errors.nationalPension)
                  }
                  helperText={
                    formik.touched.nationalPension &&
                    formik.errors.nationalPension
                  }
                  InputProps={{
                    inputComponent: NumberFormatText,
                  }}
                  sx={{ width: '100px' }}
                />
              </Box>
              <Box>%</Box>
            </Stack>
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              sx={{
                py: 1,
                borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
              }}
            >
              <Box sx={{ width: '180px' }} textAlign="center">
                <FormLabel sx={{ px: 1 }}>건강보험</FormLabel>
              </Box>
              <Box>
                <TextField
                  size="small"
                  required
                  variant="outlined"
                  fullWidth
                  id="healthInsurance"
                  name="healthInsurance"
                  value={formik.values.healthInsurance}
                  onChange={(e) =>
                    formik.setFieldValue(
                      'healthInsurance',
                      Number(e.target.value),
                    )
                  }
                  error={
                    formik.touched.healthInsurance &&
                    Boolean(formik.errors.healthInsurance)
                  }
                  helperText={
                    formik.touched.healthInsurance &&
                    formik.errors.healthInsurance
                  }
                  InputProps={{
                    inputComponent: NumberFormatText,
                  }}
                  sx={{ width: '100px' }}
                />
              </Box>
              <Box>%</Box>
            </Stack>
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              sx={{
                py: 1,
                borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
              }}
            >
              <Box sx={{ width: '180px' }} textAlign="center">
                <FormLabel sx={{ px: 1 }}>고용보험</FormLabel>
              </Box>
              <Box>
                <TextField
                  size="small"
                  required
                  variant="outlined"
                  fullWidth
                  id="employeInsurance"
                  name="employeInsurance"
                  onChange={(e) =>
                    formik.setFieldValue(
                      'employeInsurance',
                      Number(e.target.value),
                    )
                  }
                  value={formik.values.employeInsurance}
                  error={
                    formik.touched.employeInsurance &&
                    Boolean(formik.errors.employeInsurance)
                  }
                  helperText={
                    formik.touched.employeInsurance &&
                    formik.errors.employeInsurance
                  }
                  InputProps={{
                    inputComponent: NumberFormatText,
                  }}
                  sx={{ width: '100px' }}
                />
              </Box>
              <Box>%</Box>
            </Stack>
            <Stack direction="row" spacing={1} alignItems="center">
              <Box sx={{ width: '180px' }} textAlign="center">
                <FormLabel sx={{ px: 1 }}>산재보험</FormLabel>
              </Box>
              <Box>
                <TextField
                  size="small"
                  required
                  variant="outlined"
                  fullWidth
                  id="accidentInsurance"
                  name="accidentInsurance"
                  onChange={(e) =>
                    formik.setFieldValue(
                      'accidentInsurance',
                      Number(e.target.value),
                    )
                  }
                  value={formik.values.accidentInsurance}
                  error={
                    formik.touched.accidentInsurance &&
                    Boolean(formik.errors.accidentInsurance)
                  }
                  helperText={
                    formik.touched.accidentInsurance &&
                    formik.errors.accidentInsurance
                  }
                  InputProps={{
                    inputComponent: NumberFormatText,
                  }}
                  sx={{ width: '100px' }}
                />
              </Box>
              <Box>%</Box>
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Button
            type="button"
            variant="contained"
            color="secondary"
            onClick={handleCreate}
          >
            적용
          </Button>
          <Button type="submit" variant="contained" color="primary">
            일괄 적용
          </Button>
          <Button
            type="button"
            variant="contained"
            color="default"
            onClick={handleClose}
          >
            취소
          </Button>
        </DialogActions>
      </Box>
    </BootstrapDialog>
  );
};

export default FourMajorInsuranceModalContainer;
